import { z } from 'zod';
import { ApiError } from '@geefamily/api';
import type { CheckoutRequest, MarketProduct } from './api';

export interface PendingCheckout {
  request: CheckoutRequest;
  product: MarketProduct;
}

const storageKey = (userId: string) => `geefamily:market:checkout:${userId}`;

const pendingCheckoutSchema = z
  .object({
    request: z.object({
      productId: z.uuid(),
      requestId: z.uuid(),
      expectedCoinPrice: z.number().int().min(1).max(1_000_000),
      deliveryDetails: z.string().max(2000).nullable(),
    }),
    product: z.object({
      id: z.uuid(),
      title: z.string().min(1),
      description: z.string(),
      category: z.string(),
      imageUrl: z.string().nullable(),
      coinPrice: z.number().int().min(1).max(1_000_000),
      stock: z.number().int().min(0).max(1_000_000).nullable(),
      isPublished: z.boolean(),
      requiresDeliveryDetails: z.boolean(),
      deliveryInstructions: z.string().nullable(),
      displayOrder: z.number().int(),
      version: z.number().int().nonnegative(),
    }),
  })
  .refine(
    ({ request, product }) =>
      request.productId === product.id && request.expectedCoinPrice === product.coinPrice,
  );

export function readPendingCheckout(userId: string): PendingCheckout | null {
  try {
    const stored = sessionStorage.getItem(storageKey(userId));
    if (!stored) return null;
    const value: unknown = JSON.parse(stored);
    const parsed = pendingCheckoutSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Persist BEFORE dispatch; a reload after a lost response must not debit again. */
export function savePendingCheckout(userId: string, value: PendingCheckout | null) {
  if (value) sessionStorage.setItem(storageKey(userId), JSON.stringify(value));
  else sessionStorage.removeItem(storageKey(userId));
}

export function isUncertainCheckout(error: unknown) {
  return (
    !(error instanceof ApiError) ||
    error.status >= 500 ||
    error.status === 408 ||
    error.status === 429
  );
}

export function checkoutError(error: unknown) {
  if (isUncertainCheckout(error))
    return 'Siparişin sonucunu henüz doğrulayamadık. Aynı işlemi güvenle tekrar kontrol et; yeni bir sipariş oluşturulmaz.';
  if (error instanceof ApiError) {
    if (error.status === 401) return 'Oturumun sona erdi. Lütfen yeniden giriş yap.';
    if (error.status === 409)
      return 'Ürün veya bakiyen değişti. Güncel bilgileri yükledik; siparişini yeniden incele.';
    if (error.status === 404) return 'Bu ürün artık kullanılamıyor.';
    return error.message;
  }
  return 'Sipariş oluşturulamadı.';
}
